import { useContext, useEffect, useState } from 'react';

import CircleAddedIcon from '@/assets/all-icons/circle-added-icon';
import CircleNotAddedIcon from '@/assets/all-icons/circle-not-added-icon';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import ArtlistImage from '@/shared-components/cards/components/ArtlistImage';
import Text from '@/shared-components/text/components/Text';

import './styles/ArtlistItem.css';

const EMPTY_ARTLIST_ARTWORKS = [];

export default function ArtlistItem({ artlistData, close }) {
  const t = useTranslations('common.shared.artwork_view.add_to_artlist');
  const { stateHandler, cacheHandler } = useStateHandler();
  const { artworkData } = useContext(ArtworkDataContext);
  const safeArtlistData = artlistData ?? {};
  const artworkId = artworkData?._id;
  const artlistArtworks = safeArtlistData.artlist_artworks ?? EMPTY_ARTLIST_ARTWORKS;
  const getIsArtworkInArtlist = () => {
    if (!artworkId) return false;
    if (typeof safeArtlistData.artlist_contains_artwork === 'boolean') {
      return safeArtlistData.artlist_contains_artwork;
    }
    return artlistArtworks.some(artlistArtwork => artlistArtwork?._id === artworkId);
  };
  const [isArtworkInArtlist, setIsArtworkInArtlist] = useState(getIsArtworkInArtlist);
  const [artworksCount, setArtworksCount] = useState(
    typeof safeArtlistData.artlist_artworks_total === 'number'
      ? safeArtlistData.artlist_artworks_total
      : artlistArtworks.filter(Boolean).length
  );
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setIsArtworkInArtlist(getIsArtworkInArtlist());
    setArtworksCount(
      typeof safeArtlistData.artlist_artworks_total === 'number'
        ? safeArtlistData.artlist_artworks_total
        : artlistArtworks.filter(Boolean).length
    );
  }, [artlistArtworks, artworkId, safeArtlistData.artlist_artworks_total, safeArtlistData.artlist_contains_artwork]);

  async function toggleArtworkInArtlist() {
    if (isProcessing || !artworkId || !safeArtlistData?._id) return;

    const previousState = isArtworkInArtlist;
    const previousCount = artworksCount;
    const nextState = !previousState;

    setIsProcessing(true);
    setIsArtworkInArtlist(nextState);
    setArtworksCount(prev => {
      if (previousState) {
        return Math.max(prev - 1, 0);
      }
      return prev + 1;
    });

    try {
      if (nextState) {
        await UserService.addArtworkToArtlist(artworkData, safeArtlistData._id);
        cacheHandler.triggerAction("ADD_TO_ARTLIST_FROM_ARTWORK");
        const artworkTitle = artworkData?.artwork_about?.artwork_title ?? '';
        const artlistTitle = safeArtlistData?.artlist_title ?? '';
        stateHandler.set("temporalPopup", {
          text: `${artworkTitle} ${t('temporal.added_to_artlist')} ${artlistTitle}`.trim(),
          type: "navbar-present"
        });
        if (typeof close === 'function') {
          close();
        }
      } else {
        await UserService.removeArtworkFromArtlist(artworkData, safeArtlistData._id);
        cacheHandler.triggerAction("ADD_TO_ARTLIST_FROM_ARTWORK");
        const artworkTitle = artworkData?.artwork_about?.artwork_title ?? '';
        const artlistTitle = safeArtlistData?.artlist_title ?? '';
        stateHandler.set("temporalPopup", {
          text: `${artworkTitle} ${t('temporal.removed_from_artlist')} ${artlistTitle}`.trim(),
          type: "navbar-present"
        });
      }
    } catch (error) {
      setIsArtworkInArtlist(previousState);
      setArtworksCount(previousCount);
      stateHandler.set("temporalPopup", {
        text: t('temporal.generic_error'),
        type: "navbar-present"
      });
    } finally {
      setIsProcessing(false);
    }
  }

  if (!artlistData) {
    return null;
  }

  return (
    <>
      <div className="add-to-artlist-artlist-item__container" onClick={toggleArtworkInArtlist}>
        <ArtlistImage className="add-to-artlist-artlist-item__image" image={safeArtlistData.artlist_image?.image_id}/>

        <div className="add-to-artlist-artlist-item__info">
          <Text className="add-to-artlist-artlist-item__title dots-on-overflow">
            {safeArtlistData.artlist_title}
          </Text>

          <Text className="add-to-artlist-artlist-item__count">
            {t('items_count', { count: artworksCount })}
          </Text>
        </div>

        {isArtworkInArtlist ? (
          <CircleAddedIcon className="add-to-artlist-artlist-item__status-icon add-to-artlist-artlist-item__status-icon--added" />
        ) : (
          <CircleNotAddedIcon className="add-to-artlist-artlist-item__status-icon add-to-artlist-artlist-item__status-icon--not-added" />
        )}
      </div>
    </>
  );
}
